import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'sonner';
import {useGetBuyers, useGetCampaignById, usePatchBlock} from '@tinct/core-api';
import {EmbeddedBlock, PatchBlock} from '@tinct/core-api-models';
import {useConfig} from "@tinct/core-config";
import {useJobStore} from '@tinct/web-layout';
import {BUYER_IMPORT_JOB_TAG} from '@tinct/web-feature-companies';
import {campaignHasGeneratedPages} from '../../../utils/campaign-has-generated-pages';
import {isBlockPersonalizationEnabled} from '../../../utils/is-block-personalization-enabled';

type EditorFormData = {
  blocks: EmbeddedBlock[];
};

type UseCampaignEditorOptions = {
  campaignId: string;
};

/** What outdated the live pages — selects the notice's copy and CTA. */
export type ChangesNotLiveReason = 'block' | 'customer-library';

// The per-block PATCH surface the sidebar edits. Diffed against the previous
// block so each save only carries what actually changed.
const PATCHABLE_BLOCK_FIELDS = [
  'enabled',
  'track',
  'eventName',
  'greyscale',
  'dark',
  'largeLogo',
  'logoFillColor',
] as const satisfies readonly (keyof EmbeddedBlock & keyof PatchBlock)[];

const diffPatchableFields = (
  updated: EmbeddedBlock,
  previous: EmbeddedBlock | undefined,
): PatchBlock => {
  const patch: Record<string, unknown> = {};
  for (const field of PATCHABLE_BLOCK_FIELDS) {
    if (!previous || updated[field] !== previous[field]) {
      patch[field] = updated[field];
    }
  }
  return patch as PatchBlock;
};

export const useCampaignEditor = ({ campaignId }: UseCampaignEditorOptions) => {
  const { data: campaign } = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        id: null,
        name: null,
        status: null,
        outdatedStatus: null,
        landingPage: {
          fields: {
            url: null,
            currentVersion: {
              fields: {
                htmlKeys: null,
              },
            },
          },
        },
        organization: {
          fields: {
            id: null,
          }
        },
        blocks: {
          limit: 200,
        },
        statistics: {
          fields: {
            totalPagesCount: null,
          },
        },
      },
    },
    { enabled: !!campaignId }
  );

  const form = useForm<EditorFormData>({
    defaultValues: {
      blocks: [],
    },
  });

  const {
    watch,
    setValue,
    getValues,
    reset,
  } = form;

  const { isFeatureEnabled } = useConfig();
  const logoFeatureEnabled = isFeatureEnabled('logosPersonalization');

  const pagesCount = campaign?.statistics?.totalPagesCount ?? 0;

  // Whether emptying the customer library outdates already-generated variant
  // pages. Only then do we inform the user their changes aren't live.
  const isLiveWithPages = campaignHasGeneratedPages(campaign);

  // Block edits (the personalization toggle included) never outdate the
  // generated variants — they exist for every block regardless — but they are
  // a serve-time publish input, so the server flags the campaign's published,
  // in-sync pages for republication. Only then is there anything to surface.
  const isPublished = campaign?.status === 'published';

  // The informative "changes aren't live yet" popup is shown once per reason
  // per edit session, after the first action of that kind that outdates the
  // existing pages — each reason carries different copy, so editing the scope
  // and emptying the customers library are worth surfacing separately. The
  // reason is never reset on dismiss: keeping it set holds the copy stable
  // through the dialog's close animation.
  const [showChangesNotLive, setShowChangesNotLive] = useState(false);
  const [noticeReason, setNoticeReason] =
    useState<ChangesNotLiveReason>('block');
  const shownNoticesRef = useRef(new Set<ChangesNotLiveReason>());

  const notifyChangesNotLive = useCallback(
    (reason: ChangesNotLiveReason) => {
      // A block edit only outdates published pages (republication); a library
      // change outdates any generated page (regeneration).
      const isRelevant = reason === 'block' ? isPublished : isLiveWithPages;
      if (!isRelevant || shownNoticesRef.current.has(reason)) return;
      shownNoticesRef.current.add(reason);
      setNoticeReason(reason);
      setShowChangesNotLive(true);
    },
    [isLiveWithPages, isPublished]
  );

  // A customers-library change (a buyer removed or imported) that makes the
  // studio re-match the campaign's logos (see useLogoRematch) rewrites the
  // generated variants in place, so the same notice applies; the re-match job
  // does the work, the notice only informs.
  const notifyCustomerLibraryChanged = useCallback(
    () => notifyChangesNotLive('customer-library'),
    [notifyChangesNotLive]
  );

  const formBlocks = watch('blocks');

  // Hide customer_logo blocks when the feature is off — display only. Patch
  // payloads are built from `formBlocks` so hidden blocks are never dropped
  // server-side.
  const blocks = useMemo(
    () =>
      logoFeatureEnabled
        ? formBlocks
        : formBlocks.filter((block) => block.type !== 'customer_logo'),
    [formBlocks, logoFeatureEnabled],
  );

  // A customer_logo block can be enabled and its import modal closed without
  // uploading anything. Surface that as `isLogoPoolEmpty` so the funnel can
  // block "Next step". The enabled-block half comes from the optimistic form
  // state, so the button reacts instantly to the toggle; the pool count comes
  // from the buyers query below.
  const hasEnabledLogoBlock = blocks.some(
    (block) =>
      block.type === 'customer_logo' && isBlockPersonalizationEnabled(block)
  );
  const organizationId = campaign?.organization?.id;
  const { data: buyersData, refetch: refetchBuyers } = useGetBuyers(
    {
      offset: 0,
      limit: 1,
      filters: { organizationId },
      fields: { id: null },
    },
    { enabled: hasEnabledLogoBlock && !!organizationId }
  );

  // The `['buyers']` invalidation on import completion lives in the import
  // modal's job-alert hook, which unmounts with the logo-block sidebar section
  // — a backgrounded import finishing while another block is selected would
  // leave this query's count stale forever (staleTime: Infinity) and the
  // "Next step" guard stuck. Refetch on the running→terminal edge of the
  // globally tracked import job instead.
  const isImportingBuyers = useJobStore((s) =>
    s.jobs.some((job) => job.tag === BUYER_IMPORT_JOB_TAG && job.status === 'running')
  );
  const wasImportingBuyersRef = useRef(isImportingBuyers);
  useEffect(() => {
    if (wasImportingBuyersRef.current && !isImportingBuyers) {
      void refetchBuyers();
    }
    wasImportingBuyersRef.current = isImportingBuyers;
  }, [isImportingBuyers, refetchBuyers]);

  // Only true once the pool is confirmed empty — false while loading.
  const isLogoPoolEmpty =
    hasEnabledLogoBlock && buyersData?.pagination.count === 0;

  // Optimistic-update bookkeeping. `patchSeq` is a monotonically increasing id
  // stamped on each PATCH; a response may only sync the form when no newer
  // patch has been sent since, otherwise a slow response would clobber a newer
  // optimistic value (toggle on → request 1 → toggle off → request 2 →
  // response 1 flips the switch back on). `pendingPatches` counts requests in
  // flight so query refetches can't clobber either.
  const patchSeqRef = useRef(0);
  const pendingPatchesRef = useRef(0);

  // Render-visible mirror of `pendingPatchesRef`. The ref stays because the
  // form-sync effect below reads it synchronously; this drives the funnel's
  // "Next step" guard, which must go disabled the moment a patch leaves — a
  // block edit that outdates live pages is immediately followed by the
  // "changes aren't live" popup, and the user must not slip past in between.
  // `usePatchCampaign().isPending` can't serve: with two toggles in flight the
  // first response flips it back to false while the second is still running.
  const [pendingPatchCount, setPendingPatchCount] = useState(0);

  const startPatch = useCallback(() => {
    pendingPatchesRef.current++;
    setPendingPatchCount((count) => count + 1);
    return ++patchSeqRef.current;
  }, []);

  const endPatch = useCallback(() => {
    pendingPatchesRef.current--;
    setPendingPatchCount((count) => count - 1);
  }, []);

  // Sync form with campaign data when it loads. Skip while a patch is in
  // flight: the refetch triggered by an earlier patch's invalidation may not
  // include the in-flight change yet.
  useEffect(() => {
    if (campaign?.blocks && pendingPatchesRef.current === 0) {
      reset({ blocks: campaign.blocks });
    }
  }, [campaign?.blocks, reset]);

  const { mutateAsync: patchBlock } = usePatchBlock();

  // Settle the form after a PATCH response (clears dirty state, keeping the
  // optimistic values — the campaign invalidation brings the authoritative
  // blocks once every patch has landed), unless a newer patch has been sent
  // since — its optimistic value must win.
  const syncFromResponse = useCallback(
    (seq: number) => {
      if (seq !== patchSeqRef.current) return;
      reset({ blocks: getValues('blocks') });
      notifyChangesNotLive('block');
    },
    [reset, getValues, notifyChangesNotLive]
  );

  // Block edits are saved immediately through the per-block PATCH. They never
  // trigger a variant regeneration — variants exist for every block, enabled
  // or not, and the studio applies the toggle by re-filtering client-side. On
  // a published campaign the server flags the pages for republication and the
  // user is informed afterwards (via the popup).
  const handleBlockUpdate = useCallback(
    (updatedBlock: EmbeddedBlock) => {
      if (!updatedBlock.id) return;
      const blockId = updatedBlock.id;

      // Optimistic update: apply to the form immediately, then persist. On
      // failure, roll back only the failed block — against the latest form
      // state, so a concurrent successful update isn't wiped out.
      const previousBlock = formBlocks.find((block) => block.id === blockId);
      const updatedBlocks = formBlocks.map((block) =>
        block.id === blockId ? updatedBlock : block
      );
      setValue('blocks', updatedBlocks, { shouldDirty: true });

      // mutateAsync, not mutate-with-callbacks: consecutive mutate() calls on
      // one hook resubscribe the observer, so only the LAST call's callbacks
      // fire — a quick double toggle would lose an endPatch and leave the
      // pending-patch counter (and the funnel's "Next step" guard) stuck.
      // The per-call promise always settles.
      const seq = startPatch();
      patchBlock({
        blockId,
        campaignId,
        data: diffPatchableFields(updatedBlock, previousBlock),
      })
        .then(() => syncFromResponse(seq))
        .catch(() => {
          if (previousBlock) {
            setValue(
              'blocks',
              getValues('blocks').map((block) =>
                block.id === blockId ? previousBlock : block
              ),
              { shouldDirty: true }
            );
          }
          toast.error(
            `Could not save changes to ${updatedBlock.label || 'this block'}. Please try again.`,
            { position: 'bottom-center' }
          );
        })
        .finally(endPatch);
    },
    [formBlocks, setValue, getValues, patchBlock, campaignId, syncFromResponse, startPatch, endPatch]
  );

  const dismissChangesNotLive = useCallback(() => {
    setShowChangesNotLive(false);
  }, []);

  return {
    campaign,
    blocks,
    isSaving: pendingPatchCount > 0,
    isLogoPoolEmpty,
    pagesCount,
    handleBlockUpdate,
    showChangesNotLiveModal: showChangesNotLive,
    changesNotLiveReason: noticeReason,
    notifyCustomerLibraryChanged,
    dismissChangesNotLive,
  };
};
